import { ComponentType, lazy, Suspense, useMemo } from 'react';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { Navigate, Route, Routes } from 'react-router-dom';
import MeCourses from '../screens/dashboad/meCourses/MeCourses';
import NavbarWrapperLayout from './NavbarWrapperLayout';
import ToolbarLayout from './ToolbarLayout';
import { useAuthCore } from '../../core/modules/auth';
import { SIGNIN } from '../../core/router/routes';
import Welcome from '../screens/dashboad/Welcome';
import Home from '../screens/dashboad/Home';
import MeCourse from '../screens/dashboad/meCourses/MeCourse';
import { ToastContainer } from 'react-toastify';
import UserDialog from '../components/UserDialog';
import PageTransition from '../components/PageTransition';
import MobileTabBar, { MOBILE_TAB_BAR_HEIGHT } from './MobileTabBar';
import useNewNavigation from '../../core/hooks/useNewNavigation';
import useThemeMediaQuery from '../../core/hooks/useThemeMediaQuery';
import BrandLoader from '../components/BrandLoader';
import useCourseTheme from '../../core/hooks/useCourseTheme';
import { defaultTheme } from '../styles/defaultTheme';

// Si se publicó una versión nueva con la pestaña abierta, los archivos viejos ya no existen: se recarga una vez
const RELOADED_KEY = 'chunk-reloaded';
const lazyScreen = (factory: () => Promise<{ default: ComponentType<any> }>) =>
  lazy(() =>
    factory()
      .then((module) => {
        sessionStorage.removeItem(RELOADED_KEY);
        return module;
      })
      .catch((error) => {
        if (!sessionStorage.getItem(RELOADED_KEY)) {
          sessionStorage.setItem(RELOADED_KEY, '1');
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw error;
      }),
  );

// Pantallas que no se usan al entrar (admin, herramientas, calendario, reportes): se bajan recién al abrirlas
const Users = lazyScreen(() => import('../screens/dashboad/users/Users'));
const Courses = lazyScreen(() => import('../screens/dashboad/courses/Courses'));
const User = lazyScreen(() => import('../screens/dashboad/users/User'));
const Course = lazyScreen(() => import('../screens/dashboad/courses/Course'));
const Reports = lazyScreen(() => import('../screens/dashboad/automation/Reports'));
const Subscriptions = lazyScreen(() => import('../screens/dashboad/automation/Subscriptions'));
const Templates = lazyScreen(() => import('../screens/dashboad/templates/Templates'));
const Notifications = lazyScreen(() => import('../screens/dashboad/Notifications'));
const Google = lazyScreen(() => import('../screens/dashboad/Google'));
const MailSecuence = lazyScreen(() => import('../screens/dashboad/mailSecuence/MailSecuence'));
const MePositionCalculator = lazyScreen(() => import('../screens/dashboad/meCourses/MePositionCalculator'));
const Tools = lazyScreen(() => import('../screens/dashboad/tools/Tools'));
const PositionCalculator = lazyScreen(() => import('../screens/dashboad/tools/PositionCalculator'));
const DiagnosticAnswers = lazyScreen(() => import('../screens/dashboad/tools/DiagnosticAnswers'));
const TradingMetrics = lazyScreen(() => import('../screens/dashboad/tools/TradingMetrics'));
const LiquidAssets = lazyScreen(() => import('../screens/dashboad/tools/LiquidAssets'));
const RealEstate = lazyScreen(() => import('../screens/dashboad/tools/RealEstate'));
const FinancialGoalsAdmin = lazyScreen(() => import('../screens/dashboad/tools/FinancialGoals'));
const MeForum = lazyScreen(() => import('../screens/dashboad/meCourses/MeForum'));
const MeCalendar = lazyScreen(() => import('../screens/dashboad/meCourses/MeCalendar'));
const Tasks = lazyScreen(() => import('../screens/dashboad/mailSecuence/Tasks'));
const MeReports = lazyScreen(() => import('../screens/dashboad/meCourses/MeReports'));
const MeAlliances = lazyScreen(() => import('../screens/dashboad/meCourses/MeAlliances'));
const SendAnnouncement = lazyScreen(() => import('../screens/dashboad/SendAnnouncement'));
const AnnouncementDetail = lazyScreen(() => import('../screens/announcement/AnnouncementDetail'));
const MeDiagnosticQuestions = lazyScreen(() => import('../screens/dashboad/meCourses/MeDiagnosticQuestions'));
const MeTradingMetrics = lazyScreen(() => import('../screens/dashboad/meCourses/MeTradingMetrics'));
const MeLiquidAssets = lazyScreen(() => import('../screens/dashboad/meCourses/MeLiquidAssets'));
const MeRealEstate = lazyScreen(() => import('../screens/dashboad/meCourses/MeRealEstate'));
const MeFinancialGoals = lazyScreen(() => import('../screens/dashboad/meCourses/MeFinancialGoals'));
const MeFinancialMetrics = lazyScreen(() => import('../screens/dashboad/meCourses/MeFinancialMetrics'));
const FinancialMetricsAdmin = lazyScreen(() => import('../screens/dashboad/tools/FinancialMetrics'));

const Root = styled('div')(() => ({
  '& .container': {
    maxWidth: '1570px',
    width: '100%',
    margin: '0 auto',
  },
}));

const Layout = () => {
  const { token } = useAuthCore();
  const { enabled: newNavigation } = useNewNavigation();
  const isPhone = useThemeMediaQuery((theme) => theme.breakpoints.down('md'));
  const showTabBar = newNavigation && isPhone;
  // Dentro de un curso con color propio, primary y secondary pasan a ser el del curso: botones, barras, selección, herramientas
  const courseTheme = useCourseTheme();
  const pageTheme = useMemo(
    () => (courseTheme
      ? createTheme(defaultTheme, {
          palette: {
            primary: { main: courseTheme.main, dark: courseTheme.dark, light: courseTheme.tintBorder, contrastText: '#fff' },
            secondary: { main: courseTheme.main, dark: courseTheme.dark, light: courseTheme.tintBorder, contrastText: '#fff' },
          },
        })
      : defaultTheme),
    [courseTheme],
  );

  if (!token) {
    return <Navigate to={SIGNIN} replace />;
  }

  return (
    <Root id="fuse-layout" className="w-full flex">
      <ToastContainer />
      <div className="flex flex-auto min-w-0">
        <NavbarWrapperLayout />

        <main
          id="fuse-main"
          className="flex flex-col flex-auto min-h-full min-w-0 relative z-10"
        >
          <ToolbarLayout />

          <ThemeProvider theme={pageTheme}>
          <div className="flex flex-col flex-auto min-h-0 relative z-10">
            <PageTransition>
              <Suspense fallback={<BrandLoader fullHeight message={null} />}>
              <Routes>
                <Route index element={<Welcome />} />
                <Route path="/inicio" element={<Home />} />
                <Route path="/google" element={<Google />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/me/courses" element={<MeCourses />} />
                <Route path="/me/courses/:id" element={<MeCourse />} />
                <Route path="/me/course/:id/classes" element={<MeCourse />} />
                <Route path="/me/courses/:id/classes" element={<MeCourse />} />
                <Route path="/me/course/:id/position-calculator" element={<MePositionCalculator />} />
                <Route path="/me/courses/:id/position-calculator" element={<MePositionCalculator />} />
                <Route path="/me/course/:id/alliance" element={<MeAlliances />} />
                <Route path="/me/course/:id/forum" element={<MeForum />} />
                <Route path="/me/courses/:id/forum" element={<MeForum />} />
                <Route path="/me/course/:id/calendar" element={<MeCalendar />} />
                <Route path="/me/courses/:id/calendar" element={<MeCalendar />} />
                <Route path="/me/course/:id/reports" element={<MeReports />} />
                <Route path="/me/courses/:id/reports" element={<MeReports />} />
                <Route path="/automation/reports" element={<Reports />} />
                <Route path="/automation/subscriptions" element={<Subscriptions />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id" element={<User />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:id" element={<Course />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/mailSecuence" element={<MailSecuence />} />
                <Route path="/mailSecuence/:id" element={<Tasks />} />
                <Route path="/tools" element={<Tools />} />
                <Route path="/tools/position-calculator" element={<PositionCalculator />} />
                <Route path="/tools/diagnostic-answers" element={<DiagnosticAnswers />} />
                <Route path="/tools/trading-metrics" element={<TradingMetrics />} />
                <Route path="/tools/liquid-assets" element={<LiquidAssets />} />
                <Route path="/tools/real-estate" element={<RealEstate />} />
                <Route path="/tools/financial-goals" element={<FinancialGoalsAdmin />} />
                <Route path="/tools/financial-metrics" element={<FinancialMetricsAdmin />} />
                <Route path="/announcements" element={<SendAnnouncement />} />
                <Route path="/announcements/:id" element={<AnnouncementDetail />} />
                                <Route path="/me/course/:id/diagnostic-questions" element={<MeDiagnosticQuestions />} />
                  <Route path="/me/courses/:id/diagnostic-questions" element={<MeDiagnosticQuestions />} />
                  <Route path="/me/course/:id/trading-metrics" element={<MeTradingMetrics />} />
                  <Route path="/me/courses/:id/trading-metrics" element={<MeTradingMetrics />} />
                  <Route path="/me/course/:id/liquid-assets" element={<MeLiquidAssets />} />
                  <Route path="/me/courses/:id/liquid-assets" element={<MeLiquidAssets />} />
                  <Route path="/me/course/:id/real-estate" element={<MeRealEstate />} />
                  <Route path="/me/courses/:id/real-estate" element={<MeRealEstate />} />
                  <Route path="/me/course/:id/financial-goals" element={<MeFinancialGoals />} />
                  <Route path="/me/courses/:id/financial-goals" element={<MeFinancialGoals />} />
                  <Route path="/me/course/:id/financial-metrics" element={<MeFinancialMetrics />} />
                  <Route path="/me/courses/:id/financial-metrics" element={<MeFinancialMetrics />} />
              </Routes>
              </Suspense>
            </PageTransition>
          </div>
          </ThemeProvider>
          {showTabBar && (
            // Espacio para que las pestañas fijas de abajo no tapen el final de la página
            <div style={{ height: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`, flexShrink: 0 }} />
          )}
        </main>
        {showTabBar && <MobileTabBar />}
      </div>
      <UserDialog />
    </Root>
  );
};

export default Layout;
